import { Box, Grid2 as Grid } from "@mui/material";
import { DropdownItem, ModalForm, WashType } from "../../../types";
import { SelectBox, TextField } from "../../common";
import { OrganizationModel } from "../../../models";
import { selectedOrgIdAtom, userAtom } from "../../../atoms";
import { useAtom } from "jotai";
import { Role, ChargeType } from "../../../enums";
import { useFetchOrganizationsQuery } from "../../../api";
import { useEffect, useMemo, useState } from "react";
import { getCreateWashTypeBodySchema } from "../../../validations";
import { useValidateForm } from "../../../hooks";

type FormData = Omit<WashType, "miscellaneous">;

type WashTypeFormProps = ModalForm<FormData, FormData> & {
  isCreateMode?: boolean;
  isAdjustments?: boolean;
};

export const WashTypeForm = ({
  id,
  isCreateMode,
  isAdjustments,
  defaultValues,
  handleEdit,
}: WashTypeFormProps) => {
  const [user] = useAtom(userAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);

  const schema = useMemo(() => {
    return getCreateWashTypeBodySchema(isAdjustments);
  }, [isAdjustments]);

  const { register, handleSubmit, errors } = useValidateForm(schema);

  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const unitOfMeasurementDropDown: DropdownItem[] = [
    {
      key: "Quantity",
      value: "quantity",
    },
    {
      key: "Per",
      value: "hours",
    },
  ];
  const chargeTypeDropdown: DropdownItem[] = [
    { key: "Trip Minimum", value: ChargeType.TripMinimum },
    { key: "Discount", value: ChargeType.LocationDiscount },
    { key: "Fee", value: ChargeType.LocationFee },
  ];
  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: user?.roleCode === Role.SuperAdmin,
  });

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      const dropdown = OrganizationModel.buildOrganizationDropdown(
        fetchOrganizationsQuery.data,
      );
      setOrganizationsDropdown(dropdown);
    } else if (fetchOrganizationsQuery.isError) {
      setOrganizationsDropdown([]);
    }
  }, [
    fetchOrganizationsQuery.isSuccess,
    fetchOrganizationsQuery.isError,
    fetchOrganizationsQuery.data,
    fetchOrganizationsQuery.error,
    fetchOrganizationsQuery.isLoading,
  ]);

  const handleEditWrapper = (
    data: Omit<
      WashType,
      "id" | "miscellaneous" | "chargeType" | "unitOfMeasurement"
    > & {
      chargeType?: string;
      unitOfMeasurement?: string;
    },
  ) => {
    const chargeType = Object.values(ChargeType).includes(
      data.chargeType as ChargeType,
    )
      ? (data.chargeType as ChargeType)
      : ChargeType.Miscellaneous;

    handleEdit({
      ...data,
      chargeType,
      unitOfMeasurement: data.unitOfMeasurement || "quantity",
      id: defaultValues ? defaultValues.id : 0,
    });
  };

  return (
    <Box
      onSubmit={handleSubmit(handleEditWrapper)}
      component={"form"}
      id={id}
      noValidate
      sx={{
        height: "100%",
      }}
    >
      <Grid
        container
        size={12}
        direction={"column"}
        gap={2}
        sx={{
          borderRadius: "10px",
          p: 3,
          mb: 2,
          width: "100%",
        }}
      >
        <Grid size={12}>
          <TextField
            label="Name"
            name="name"
            register={register}
            error={errors["name"]}
            defaultValue={defaultValues && defaultValues.name}
            maxLength={100}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Description"
            name="description"
            register={register}
            error={errors["description"]}
            defaultValue={defaultValues && defaultValues.description}
            maxLength={300}
          />
        </Grid>

        {!isAdjustments && (
          <Grid size={12}>
            <SelectBox
              label="Unit Of Measurement"
              name="unitOfMeasurement"
              register={register}
              error={errors["unitOfMeasurement"]}
              dropdown={unitOfMeasurementDropDown}
              defaultValue={defaultValues && defaultValues.unitOfMeasurement}
            />
          </Grid>
        )}

        {user?.roleCode === Role.SuperAdmin && (
          <Grid size={12}>
            <SelectBox
              label="Organization"
              name="orgId"
              register={register}
              error={errors["orgId"]}
              dropdown={organizationsDropdown}
              disabled={!isCreateMode}
              valueAsNumber={true}
              defaultValue={defaultValues && defaultValues.orgId}
            />
          </Grid>
        )}
        {!isCreateMode && (
          <input
            type="number"
            style={{ display: "none" }}
            {...register("orgId")}
            value={defaultValues?.orgId}
          />
        )}
        {isCreateMode && user?.roleCode !== Role.SuperAdmin && (
          <input
            type="number"
            style={{ display: "none" }}
            {...register("orgId")}
            value={selectedOrgId}
          />
        )}
        <Grid size={12}>
          <TextField
            label="Code"
            name="code"
            register={register}
            error={errors["code"]}
            defaultValue={defaultValues && defaultValues.code}
            maxLength={15}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            type={"number"}
            label="Rate"
            name="rate"
            register={register}
            error={errors["rate"]}
            defaultValue={defaultValues && defaultValues.rate}
          />
        </Grid>
        {isAdjustments && (
          <Grid size={12}>
            <SelectBox
              label="Adjustment"
              name="chargeType"
              register={register}
              error={errors["chargeType"]}
              dropdown={chargeTypeDropdown}
              defaultValue={(defaultValues && defaultValues.chargeType) || ""}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
